import { redactUrl } from './redact'

/**
 * The error-reporting port: what the app is allowed to say about a failure,
 * with no opinion about where it goes.
 *
 * The one-way rule is
 *   `boundaries · instrumentation · routes → error-reporter.ts → adapters/*`
 * and it is enforced by the fact that this file imports nothing but its
 * zero-dependency sibling `redact.ts`. It is byte-identical and safe in all
 * THREE runtimes — browser, node server, and the edge runtime `src/middleware.ts`
 * runs on — because it contains no SDK, no node built-in and no `fetch`.
 * Adding a third-party import here is how the client bundle grows
 * `posthog-node`; don't. There is a `scripts/verify/observability.mjs`
 * assertion that pins this.
 *
 * ── Why a globalThis registry and not a module singleton ──────────
 * Next compiles `instrumentation.ts` into its own webpack layer
 * (`next/dist/build/entries.js`: `layer: WEBPACK_LAYERS.instrument`). A
 * module-level `let` set from there is a DIFFERENT instance from the one a
 * Server Component sees. `src/lib/posthog-logs.ts` already works around exactly
 * this by hanging the OTel logger on `globalThis`; this does the same, for the
 * same reason. A plain `let` here would look correct, type-check, and report
 * nothing at all.
 *
 * ── Why intent-named functions ────────────────────────────────────
 * Same reasoning as `src/lib/email/send.ts`: it is hard to double-report a
 * server error from the client when the function that would do it knows it is
 * the client and refuses. The digest rule in `reportBoundaryError` is the whole
 * point of the split; a single generic `report()` would have to take a flag,
 * and flags get passed wrong.
 *
 * ── The invariant ─────────────────────────────────────────────────
 * NOTHING here throws. An error boundary that throws while reporting an error
 * is the one failure with no fallback left. `console.error` runs first and
 * unconditionally, so a dead analytics pipe never costs us the local signal
 * too — which is also why call sites no longer carry their own `console.error`.
 */

export type ErrorSource =
  | 'client-boundary'
  | 'global-boundary'
  | 'request'
  | 'handled'

/** Flat, primitive-only. Anything richer is a stack trace pretending to be a prop. */
export type ErrorProps = Record<string, string | number | boolean | null | undefined>

export interface ErrorReport {
  readonly error: unknown
  readonly source: ErrorSource
  /** Next's `error.digest` — the only key that ties a client boundary to a server throw. */
  readonly digest?: string
  readonly distinctId?: string
  readonly props?: ErrorProps
}

export interface ErrorReporter {
  capture(report: ErrorReport): void | Promise<void>
  flush(): Promise<void>
  /**
   * The consent chokepoint (`docs/DECISIONS.md` #12). Optional, because only the
   * browser adapter has anything to switch off. Implemented there as
   * `opt_in_capturing()` / `opt_out_capturing()`, which is deliberately BROADER
   * than error reporting: it also gates the product-analytics `capture()` calls
   * that still import posthog-js directly, so the future cookie banner has one
   * call to make and not eleven.
   */
  setEnabled?(enabled: boolean): void
}

// ── Registry ────────────────────────────────────────────────────

interface Registry {
  reporter: ErrorReporter | null
  enabled: boolean
}

function registry(): Registry {
  const g = globalThis as typeof globalThis & { __crmpeepsErrorReporting?: Registry }
  return (g.__crmpeepsErrorReporting ??= { reporter: null, enabled: true })
}

/** Called ONLY from `src/instrumentation.ts` and `src/instrumentation-client.ts`. */
export function setErrorReporter(reporter: ErrorReporter | null): void {
  registry().reporter = reporter
}

/**
 * The single switch a cookie-consent banner flips. No caller today — the default
 * is `true` and behaviour is unchanged. When the banner lands
 * (`docs/DECISIONS.md` #12) it calls this and nothing else changes anywhere.
 */
export function setErrorReportingEnabled(enabled: boolean): void {
  const r = registry()
  r.enabled = enabled
  try {
    r.reporter?.setEnabled?.(enabled)
  } catch {
    /* consent must not be able to throw either */
  }
}

// ── Internals ───────────────────────────────────────────────────

/**
 * The local signal, emitted before anything is dispatched and regardless of
 * whether the pipe is alive. This is why call sites no longer carry their own
 * `console.error`: a dead reporter must never cost us the terminal too.
 */
function logLocally(prefix: string, error: unknown, props?: ErrorProps): void {
  try {
    if (props && Object.keys(props).length) console.error(prefix, error, props)
    else console.error(prefix, error)
  } catch {
    /* even console can be patched into throwing */
  }
}

/** Fire-and-forget. For paths where nothing can await (React effects). */
function dispatch(report: ErrorReport): void {
  void dispatchAwaited(report)
}

/**
 * Awaited. On Vercel the function freezes the moment it responds, so a
 * fire-and-forget capture from a server path is a capture that never leaves —
 * the same reason `posthog-server.ts` is `flushAt: 1, flushInterval: 0` and the
 * Stripe webhook uses `captureImmediate`. Next's docs say the same of
 * `onRequestError`: "If you're running any async tasks … make sure they're awaited."
 */
async function dispatchAwaited(report: ErrorReport): Promise<void> {
  const { reporter, enabled } = registry()
  if (!enabled || !reporter) return
  try {
    await reporter.capture(report)
  } catch {
    /* reporting must never throw */
  }
}

/**
 * The property shape every adapter sends. Lives on the port, not in an adapter,
 * because it was written out three times across two of them and the copies had
 * already started to drift — a field added for one transport is a field the
 * other silently omits.
 */
export function toEventProps(report: ErrorReport): ErrorProps {
  return {
    error_source: report.source,
    ...(report.digest ? { error_digest: report.digest } : {}),
    ...report.props,
  }
}

// ── The three ways to report ────────────────────────────────────

/**
 * A React error boundary caught a render error.
 *
 * ⚠️ A `digest` means the error originated on the SERVER. React redacted it
 * before it crossed the wire, so `error.message` here is the generic "An error
 * occurred in the Server Components render…" and the stack belongs to the
 * client, not to the throw site. `onRequestError` in `src/instrumentation.ts`
 * has ALREADY reported the real one, with the real stack, under the same digest.
 *
 * Sending this too opens a SECOND PostHog issue per incident: one useful, one
 * permanently unactionable. Worse, the unactionable one fingerprints identically
 * for EVERY server error in the app, so within a week it is the loudest issue in
 * the project while saying nothing at all. So the client reports only what only
 * the client knows, and the digest is echoed to the console so the local signal
 * is never lost.
 */
export function reportBoundaryError(
  error: unknown,
  meta: { boundary: 'app' | 'global'; digest?: string; props?: ErrorProps },
): void {
  logLocally(`[error-boundary:${meta.boundary}]`, error, {
    ...meta.props,
    ...(meta.digest ? { digest: meta.digest } : {}),
  })

  if (meta.digest) return

  dispatch({
    error,
    source: meta.boundary === 'global' ? 'global-boundary' : 'client-boundary',
    props: { boundary: meta.boundary, ...meta.props },
  })
}

export interface RequestErrorMeta {
  /** The route FILE path, e.g. `/app/forum/[threadId]/page`. Low cardinality. */
  routePath: string
  /** ⚠️ `'proxy'` replaced `'middleware'` in Next 16. */
  routeType: 'render' | 'route' | 'action' | 'proxy'
  routerKind: 'Pages Router' | 'App Router'
  renderSource?: string
  revalidateReason?: string
  method: string
  /** The REQUEST path, query string included — redacted here, not by the caller. */
  path: string
  digest?: string
  distinctId?: string
}

/** The server caught an error while handling a request. From `onRequestError` only. */
export async function reportRequestError(
  error: unknown,
  meta: RequestErrorMeta,
): Promise<void> {
  // ⚠️ Redaction happens HERE, not at the call site, because `meta.path` carries
  // the query string and the auth callback puts a live PKCE code in it. A caller
  // that forgets is a caller that leaks; the port does not give it the chance.
  const path = redactUrl(meta.path)

  const props: ErrorProps = {
    route_path: meta.routePath,
    route_type: meta.routeType,
    router_kind: meta.routerKind,
    request_method: meta.method,
    request_path: path,
    ...(meta.renderSource ? { render_source: meta.renderSource } : {}),
    ...(meta.revalidateReason ? { revalidate_reason: meta.revalidateReason } : {}),
    ...(meta.digest ? { error_digest: meta.digest } : {}),
  }

  logLocally(`[onRequestError] ${meta.routeType} ${meta.routePath}`, error, props)

  await dispatchAwaited({
    error,
    source: 'request',
    digest: meta.digest,
    distinctId: meta.distinctId,
    props,
  })
}

/**
 * We caught it, we handled it, and we chose not to rethrow — but it is still a
 * defect. The Stripe webhook's handler catch is the first caller: it returns a
 * 500 rather than rethrowing, so `onRequestError` never sees it and every
 * billing failure was invisible to Error Tracking.
 *
 * The ~45 Supabase log-and-swallow sites `ROADMAP.md` carries as cross-cutting
 * debt are the eventual callers; this exists so that migration is mechanical
 * when it comes.
 *
 * Returns a promise so server callers can await it before the function freezes.
 * Client callers can safely `void` it.
 */
export async function reportHandledError(
  error: unknown,
  meta: { op: string; props?: ErrorProps },
): Promise<void> {
  logLocally(`[${meta.op}]`, error, meta.props)
  await dispatchAwaited({
    error,
    source: 'handled',
    props: { op: meta.op, ...meta.props },
  })
}

/**
 * Await pending deliveries. On serverless, schedule via `after()` from
 * `next/server` alongside the existing `after(flushLogs)`.
 */
export async function flushErrorReports(): Promise<void> {
  try {
    await registry().reporter?.flush()
  } catch {
    /* a failed flush is not worth a 500 */
  }
}
